import { Select } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

import useDebounce from 'common/hooks/useDebounce';
import useScrollInfinite from 'common/hooks/useScrollInfinite';
import { getAllProjectService } from 'common/services/extends/project';

export type DropdownProgramProps = {
  options?: OptionType[];
  loading?: boolean;
  name?: string;
  placeholder?: string;
  size?: SizeType;
  value: any;
  onChange: (...event: any[]) => void;
  filterParams?: string;
  isGetOption?: boolean;
  isShowSearch?: boolean;
  multiple?: {
    allowClear?: boolean;
    defaultValue?: any;
  },
  error?: string;
  ids?: string;
  slugs?: string;
  labelInValue?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  noMargin?: boolean;
  allowClear?: boolean;
  handleLoadMore?: () => void;
  handleExternalSearch?: (value: string) => void;
};

export const DropdownProgram: React.FC<DropdownProgramProps> = ({
  name,
  loading,
  options,
  size,
  placeholder,
  isShowSearch = true,
  value,
  onChange,
  filterParams,
  isGetOption,
  multiple,
  error,
  ids,
  slugs,
  labelInValue = false,
  disabled,
  readOnly,
  noMargin,
  className,
  allowClear = true,
  handleLoadMore,
  handleExternalSearch
}) => {
  const { t } = useTranslation();
  const [txtSearch, setTxtSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [optionsData, setOptionsData] = useState<OptionType[]>(options || []);

  useDebounce(() => {
    setSearchDebounce(txtSearch);
  }, 500, [txtSearch]);

  useEffect(() => {
    if (handleExternalSearch) handleExternalSearch(searchDebounce);
  }, [searchDebounce, handleExternalSearch]);

  const {
    data: dropdownList,
    isLoading,
    fetchNextPage,
  } = useInfiniteQuery(
    [`program-${name ? `-${name}` : ''}`, {
      searchDebounce, ids, slugs
    }],
    ({ pageParam = 1 }) => getAllProjectService({
      ...(searchDebounce && { keyword: searchDebounce }),
      limit: 10,
      page: pageParam,
    }),
    {
      getNextPageParam: (lastPage) => (lastPage.meta.page + 1 <= lastPage.meta.totalPages
        ? lastPage.meta.page + 1
        : undefined),
    },
  );

  const { setNode } = useScrollInfinite(() => {
    fetchNextPage();
    if (handleLoadMore) {
      handleLoadMore();
    }
  });

  useEffect(() => {
    if (dropdownList) {
      const data = dropdownList.pages.reduce((prev: OptionType[], current) => [...prev,
      ...current.data.map((val) => ({
        label: val.projectData?.code || '',
        value: val.projectData?.id,
      })).filter((val) => ((filterParams ? val.value?.toString() !== filterParams : true)))], []);
      setOptionsData(data);
    }
  }, [dropdownList, filterParams, searchDebounce]);

  useEffect(() => {
    if (options) {
      setOptionsData(options.filter((
        item
      ) => item.label.toLowerCase().includes(searchDebounce.toLowerCase())));
    }
  }, [options, searchDebounce]);

  return (
    <>
      <Select
        className={`${!noMargin && 'u-mt-8'}${readOnly ? ' readonly' : ''} ${className}`}
        disabled={disabled || readOnly}
        size={size}
        style={{ width: '100%' }}
        placeholder={placeholder}
        value={value}
        labelInValue={labelInValue}
        onChange={(val) => {
          if (isGetOption) {
            if (multiple) {
              const selected = optionsData.filter((item) => val.includes(item.value));
              onChange(selected);
            } else if (val) {
              onChange(optionsData.find((item) => item.value === val));
            } else {
              onChange({
                value: undefined,
                label: undefined
              });
            }
          } else {
            onChange(val);
          }
          if (multiple) {
            setTxtSearch('');
          }
        }}
        {...multiple && {
          mode: 'multiple',
          allowClear: multiple.allowClear,
          defaultValue: multiple.defaultValue,
        }}
        dropdownMatchSelectWidth={false}
        getPopupContainer={(trigger) => trigger.parentElement}
        defaultActiveFirstOption={false}
        showSearch={isShowSearch}
        filterOption={false}
        onSearch={(val) => {
          if (isShowSearch) {
            setTxtSearch(val);
          }
        }}
        onClear={() => setTxtSearch('')}
        allowClear={allowClear}
        loading={isLoading || loading}
      >
        {
          optionsData.map(
            (option, idx) => (idx === optionsData.length - 3 ? (
              <Select.Option
                label={t(option.label)}
                value={option.value}
                key={`option-${idx.toString()}`}
                disabled={option.disabled}
              >
                <div ref={(ref) => setNode(ref)}>{t(option.label)}</div>
              </Select.Option>
            ) : (
              <Select.Option
                label={t(option.label)}
                value={option.value}
                key={`option-${idx.toString()}`}
                disabled={option.disabled}
              >
                {t(option.label)}
              </Select.Option>
            ))
          )
        }
      </Select>
      {error && (
        <span
          className="a-input_errorMessage"
        >
          {error}
        </span>
      )}
    </>
  );
};
